import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { LuChevronDown, LuCheck } from 'react-icons/lu'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import Chip from '@mui/material/Chip'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'

import useOrganizations, { orgLabel } from '../models/useOrganizations'
import OwnerAvatar from './OwnerAvatar'
import { pathAfterOrgSwitch } from '../utilities/orgSwitch'

// First-class, always-visible org-context switcher. The resource stores scope to
// activeOrgId (UX scoping), and joining an org auto-switches into it — so without a
// prominent selector a user's personal networks/nodes "disappear" with no obvious
// way back. Surfaced only when there's a choice (>1 org); selecting re-scopes the
// resource stores via setActiveOrg.
export default function OrgSelector() {
  const { organizations, activeOrgId, setActiveOrg, followed } = useOrganizations()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  if (organizations.length <= 1) return null

  const active = organizations.find(o => o.id === activeOrgId)
  const label = active ? orgLabel(active) : 'Organization'

  return (
    <>
      <Button
        color="inherit"
        onClick={e => setAnchorEl(e.currentTarget)}
        endIcon={<LuChevronDown size={16} />}
        sx={theme => ({
          letterSpacing: 0.5,
          fontWeight: 500,
          backdropFilter: theme.shape.backdropBlur,
          backgroundColor: theme.palette.transparent.lighten,
          borderRadius: theme.shape.borderRadiusLarge,
        })}
      >
        {label}
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {/* ⚠⚠ THE ITEM CARRIES ITS OWN NAME (app#212). Read from the live menu, these items
            exposed four bare `menuitem` nodes: the label and the role reach the screen as child
            markup — an icon slot, an avatar, a `ListItemText` and a `Chip` — and a screen-reader
            user choosing between them heard nothing. This choice re-scopes every panel in the app,
            including whose money the billing panel is about, so it is the wrong one to make blind.

            ⚠ `orgLabel` rather than `org.name`, which is NULL on an unnamed personal org. The
            role rides on the same string because it is the other half the eye gets from the chip. */}
        {organizations.map(org => (
          <MenuItem
            key={org.id}
            aria-label={`${orgLabel(org)} — ${org.role}`}
            selected={org.id === activeOrgId}
            onClick={() => {
              setAnchorEl(null)
              if (org.id === activeOrgId) return
              setActiveOrg(org.id)
              // ⚠ A page about one node, network or service of the OLD organization does not
              // follow the reader: it drew an empty pane (app#471). The reader goes to the org's home
              // view. See `pathAfterOrgSwitch`.
              // ⚠ `replace`, not a push: the entry that names the old object is the one that cannot
              // be drawn, so Back must not return to it.
              const next = pathAfterOrgSwitch(pathname)
              if (next) navigate(next, { replace: true })
            }}
          >
            <ListItemIcon>{org.id === activeOrgId && <LuCheck size={16} />}</ListItemIcon>
            <OwnerAvatar url={org.ownerAvatarUrl} sx={{ width: 22, height: 22, mr: 1 }} />
            <ListItemText primary={orgLabel(org)} />
            <Chip label={org.role} size="small" variant="outlined" sx={{ ml: 2 }} />
          </MenuItem>
        ))}
      </Menu>
      {/* ⚠ The app changed the organization by itself, to show what the path names (app#478: the
          link of a mail, a bookmark, Back). The active organization also decides whose usage and
          billing show, so the change is said, here by the control that shows it.
          ⚠ A click in the page does not close it (`clickaway`): the reader's first act after a mail
          link is a click in the page they asked for, and the notice is the only sign of the change.
          ⚠ "The link", not "the page": a link with a wrong network in it ends on the home view of
          this organization, and the sentence is true there too. */}
      <Snackbar
        key={followed ?? 'none'}
        open={followed === activeOrgId && !!active}
        autoHideDuration={8000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') useOrganizations.getState().set({ followed: null })
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => useOrganizations.getState().set({ followed: null })}>
          Now in {label}: the link you opened is in this organization.
        </Alert>
      </Snackbar>
    </>
  )
}
